import React from "react";
import { motion } from "framer-motion";
import {
  Users,
  Briefcase,
  ShieldCheck,
  Cpu,
  Award,
  TrendingUp
} from "lucide-react";
import Navbar from "../../components/Navbar";

const corporateTrainings = [
  {
    title: "Quality & Compliance Trainings",
    icon: ShieldCheck,
    image: "https://images.unsplash.com/photo-1556761175-129418cb2dfe",
    description:
      "Customized corporate training programs focused on ISO standards, regulatory compliance, and operational excellence.",
    topics: [
      "ISO 9001 / 14001 / 45001",
      "Internal Auditor Training",
      "Risk-Based Thinking",
      "Process Improvement"
    ]
  },
  {
    title: "Food Safety & Manufacturing",
    icon: Briefcase,
    image: "https://images.unsplash.com/photo-1581091215367-59ab6baf1c6a",
    description:
      "Industry-specific food safety and manufacturing trainings aligned with global certification requirements.",
    topics: [
      "HACCP & GMP",
      "ISO 22000 / FSSC 22000",
      "BRCGS & Global GAP",
      "Food Safety Culture"
    ]
  },
  {
    title: "IT & Cyber Security Trainings",
    icon: Cpu,
    image: "https://images.unsplash.com/photo-1518770660439-4636190af475",
    description:
      "Advanced corporate trainings designed to strengthen information security, data privacy, and IT governance.",
    topics: [
      "ISO/IEC 27001 & 27701",
      "Cyber Risk Assessment",
      "Business Continuity (ISO 22301)",
      "Cloud & Privacy Controls"
    ]
  }
];

const Corporate_Trainings = () => {
  return (
    <>
    <Navbar />
    <section className="bg-gray-50 py-14 px-4 sm:px-6 lg:px-8 overflow-hidden">
      <div className="max-w-7xl mx-auto">

        {/* Heading */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="max-w-3xl mx-auto text-center mb-20"
        >
          <h2 className="text-3xl md:text-4xl font-bold text-transparent bg-clip-text bg-[var(--bg-primary)] mb-4">
            Corporate Training Programs
          </h2>
          <div className="w-24 h-1 bg-[var(--bg-primary)] mx-auto rounded-full mb-4"></div>
          <p className="text-gray-600 text-base md:text-lg">
            MBA Solutions delivers tailored corporate trainings designed to upskill
            teams, enhance compliance, and drive sustainable business performance.
          </p>
        </motion.div>

        {/* Cards */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-10">
          {corporateTrainings.map((item, index) => {
            const Icon = item.icon;
            return (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                whileHover={{ y: -14 }}
                className="group bg-white rounded-3xl overflow-hidden shadow-xl border border-gray-100"
              >
                {/* Image */}
                <div className="relative h-52 overflow-hidden">
                  <img
                    src={item.image}
                    alt={item.title}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
                  <div className="absolute bottom-4 left-4 flex items-center gap-2 text-white">
                    <Icon size={22} />
                    <span className="font-semibold">Corporate Training</span>
                  </div>
                </div>

                {/* Content */}
                <div className="p-7">
                  <h3 className="text-xl font-bold text-gray-900 mb-3 group-hover:text-[var(--bg-primary)] transition-colors">
                    {item.title}
                  </h3>
                  <p className="text-sm text-gray-600 leading-relaxed mb-6">
                    {item.description}
                  </p>

                  <ul className="space-y-3 text-sm text-gray-700">
                    {item.topics.map((topic, i) => (
                      <li key={i} className="flex items-start gap-2">
                        <Award size={16} className="text-[var(--bg-primary)] mt-1" />
                        <span>{topic}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </motion.div>
            );
          })}
        </div>

        {/* CTA */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="mt-24 bg-white rounded-3xl shadow-xl p-12 text-center"
        >
          <div className="flex justify-center mb-6">
            <TrendingUp size={44} className="text-[var(--bg-primary)]" />
          </div>
          <h3 className="text-2xl md:text-3xl font-bold text-gray-900 mb-4">
            Upskill Your Workforce with Expert-Led Trainings
          </h3>
          <p className="text-gray-600 mb-8 max-w-2xl mx-auto">
            Our corporate training solutions are customized to your industry,
            organizational maturity, and strategic objectives.
          </p>
          <button className="inline-flex items-center gap-2 px-10 py-4 rounded-full bg-[var(--bg-primary)] text-white font-semibold shadow-lg hover:shadow-2xl hover:scale-105 transition-all">
            <Users size={18} /> Request Corporate Training
          </button>
        </motion.div>

      </div>
    </section>
    </>
  );
};

export default Corporate_Trainings;